<script>
import { Helper, debounce } from '@/mixin/helper'
import moment from 'moment'
import * as encryptJwt from '@/services/rest-api-tools/lib/encrypt-jwt/encrypt-jwt/'
import * as syncVehicleLogApi from '@/services/rest-api-sms/lib/sync-vehicle-log/sync-vehicle-log'

export default {
  name: 'SyncVehicleLog',

  props: {
    modal: {
      type: Boolean,
      required: true,
    },
  },

  mixins: [Helper],

  data() {
    return {
      totalPages: 0,
      isModalOpen: false,
      logs: [],
      loading: false,
      filter: '',
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      pageOptions: [10, 25, 50, 100],
      filteredItems: [],
      syncVehicleLogs: [],
      fields: [
        { key: 'created_on', label: 'Date Time' },
        { key: 'total_data', label: 'Total Data' },
        { key: 'total_processed', label: 'Total Processed' },
        { key: 'match', label: 'Match' },
        { key: 'unmatch', label: 'Unmatch' },
        { key: 'status', label: 'Status' },
        { key: 'detail_data', label: 'Detail Data' },
        { key: 'server_region', label: 'Region' },
        { key: 'sync_progress', label: 'Sync Progress' }, // New column
      ],
      lastPong: moment(),
      pongInterval: null,
      wsLoading: false,
      connectionKeeper: null,
      lastMessageTime: null,
    }
  },

  watch: {
    modal: {
      immediate: true,
      handler(newValue) {
        this.isModalOpen = newValue
        if (newValue) {
          this.fetchLogs()
        }
      },
    },
    isModalOpen(newValue) {
      this.$emit('update:modal', newValue)
    },
  },

  methods: {
    debounceFetchLogs: debounce(function () {
      this.fetchLogs()
    }, 500),

    async fetchLogs() {
      this.loading = true
      const params = {
        page: this.currentPage,
        limit: this.perPage,
      }

      try {
        const response = await syncVehicleLogApi.get(params)

        if (!response?.data?.paginated_result) return

        this.syncVehicleLogs = response.data.paginated_result
        this.filteredItems = response.data.paginated_result
        this.totalRows = response.metadata.total_count
        this.totalPages = response.metadata.total_pages
        this.currentPage = response.metadata.page

        this.loading = false
      } catch (error) {
        this.$toast.error('Error loading logs')
      } finally {
        const payload = {
          payload: {
            tags: [`sync-vehicle-log-${this.syncVehicleLogs[0].id}`],
          },
        }
        // sync-vehicle-log
        const encryptedPayload = await encryptJwt.store(payload)

        localStorage.setItem('payload_sync', encryptedPayload?.data)
        this.startWebSocket()
        this.loading = false
      }
    },

    resetModal() {
      this.filter = ''
      this.currentPage = 1
      this.logs = []
    },

    handleError(error) {
      if (error.response) {
        const errorStatus = error.response.status
        const errorData = error.response.data

        if (errorStatus === 400 && errorData.details) {
          errorData.details.forEach((element) => {
            this.$toast.error(`${errorData.error}: ${element.field} ${element.message}`)
          })
        } else if (errorStatus === 401) {
          this.$toast.error(errorData.error)
          localStorage.removeItem('token')
          localStorage.removeItem('tokenAccess')
          this.$router.push({ name: 'Login' })
        } else if (errorStatus === 403) {
          this.$toast.error(errorData.error)
        } else {
          this.$toast.error(errorData.error)
        }
      } else if (error.request) {
        this.$toast.error(error.response.statusText)
      } else {
        this.$toast.error(error.message)
      }
    },

    changePage(e) {
      this.currentPage = e
      this.fetchLogs()
    },

    getStatusClass(status) {
      if (status === 'Success') return 'text-success'
      if (status === 'Failed') return 'text-danger'
      return 'text-muted'
    },

    resetFilter(newPerPage) {
      this.perPage = newPerPage
      this.currentPage = 1
      this.fetchLogs()
    },

    created() {
      this.fetchLogs()
    },

    async startWebSocket() {
      try {
        if (this.$socket && this.$socket.readyState === WebSocket.OPEN) {
          await this.$store.dispatch('ws/WSDisconnect')
        }

        const wsUrl = `${process.env.VUE_APP_WS_URL}/ws?token=${localStorage.getItem('wsToken')}`
        await this.$store.dispatch('ws/WSConnect', wsUrl)

        this.wsLoading = true
        this.lastMessageTime = Date.now()

        // Keep connection alive and check for data flow
        this.connectionKeeper = setInterval(() => {
          const currentTime = Date.now()
          if (currentTime - this.lastMessageTime > 3000) {
            // No data received for 3 seconds, resend request
            const obj = {
              type: 'update_tags',
              data: localStorage.getItem('payload_sync'),
            }
            if (this.$socket?.readyState === WebSocket.OPEN) {
              this.$socket.sendObj(obj)
            } else {
              // Connection lost, reconnect
              this.startWebSocket()
            }
          }
        }, 1000)

        while (this.$socket?.readyState !== WebSocket.OPEN) {
          await new Promise((resolve) => setTimeout(resolve, 100))
        }

        const obj = {
          type: 'update_tags',
          data: localStorage.getItem('payload_sync'),
        }
        this.$socket.sendObj(obj)

        this.$socket.onmessage = (data) => {
          this.lastMessageTime = Date.now()
          const signal = JSON.parse(data.data)

          if (signal.type === 'PONG') {
            this.$socket.sendObj(obj)
            this.lastPong = moment()
            return
          }

          if (signal.type === `sync-vehicle-log-${this.syncVehicleLogs[0].id}`) {
            this.syncVehicleLogs[0].match = signal.payload.match
            this.syncVehicleLogs[0].unmatch = signal.payload.unmatch
            this.syncVehicleLogs[0].total_processed = signal.payload.total_processed
          }
        }
      } catch (error) {
        console.error('WebSocket connection error:', error)
        this.wsLoading = false
      }
    },

    isCurrentSyncItem(item) {
      return (
        this.syncVehicleLogs &&
        this.syncVehicleLogs.length > 0 &&
        item.id === this.syncVehicleLogs[0].id
      )
    },

    async downloadDetailData(url) {
      if (!url) {
        return
      }

      // Create a temporary anchor element for download
      const link = document.createElement('a')
      link.href = url
      link.target = '_blank'
      link.download = '' // This will use the filename from the URL

      // Append to body, click, and remove
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
  },

  mounted() {
    moment.locale('id')
  },

  beforeDestroy() {
    clearInterval(this.connectionKeeper)
  },

  async destroyed() {
    await this.$store.dispatch('ws/WSDisconnect')
    clearInterval(this.pongInterval)
  },
}
</script>

<template>
  <b-modal
    v-model="isModalOpen"
    id="email-log-modal"
    title="Sync Vehicle Log"
    size="xl"
    :hide-footer="true"
    @hidden="resetModal"
  >
    <div v-if="loading" class="text-center py-3">
      <b-spinner class="align-middle" small></b-spinner>
      <p class="mt-2">Loading Sync Vehicle Logs...</p>
    </div>

    <div v-else>
      <div class="table-responsive my-3 border border-1">
        <b-table
          class="datatables"
          :items="syncVehicleLogs"
          :fields="fields"
          :busy="loading"
          show-empty
          responsive="sm"
          hover
          :empty-text="$t('global.emptyTable')"
          :empty-filtered-text="$t('global.emptyFilteredTable')"
        >
          <template #cell(created_on)="data">
            {{ data.item.created_on ? momentDMHms(data.value) : '-' }}
          </template>

          <template #cell(detail_data)="data">
            <b-button
              v-if="data.item.detail_data"
              @click="downloadDetailData(data.value)"
              variant="link"
              size="sm"
              :disabled="data.item.downloading"
            >
              {{ data.item.downloading ? 'Downloading...' : 'Download' }}
            </b-button>
            <span v-else> - </span>
          </template>

          <template #cell(sync_progress)="data">
            <div v-if="isCurrentSyncItem(data.item)">
              <!-- Progress bar untuk status inprogress -->
              <div v-if="data.item.status === 'In Progress' && wsLoading">
                <b-progress
                  :value="(data.item.total_processed / data.item.total_data) * 100"
                  :max="100"
                  show-progress
                  animated
                  height="2rem"
                  class="mb-0"
                  size="lg"
                />
              </div>
              <!-- Success status -->
              <span v-else-if="data.item.status === 'Success'">
                <i class="fas fa-check text-success"></i> Completed
              </span>
              <!-- Fail status -->
              <span v-else-if="data.item.status === 'Fail'">
                <i class="fas fa-times text-danger"></i> Failed
              </span>
              <!-- Default waiting -->
              <span v-else> Waiting... ({{ data.item.status }}) </span>
            </div>
            <!-- Untuk item yang bukan current sync, tampilkan status sesuai kondisi sebenarnya -->
            <div v-else>
              <span v-if="data.item.status === 'Success'">
                <i class="fas fa-check text-success"></i> Completed
              </span>
              <span v-else-if="data.item.status === 'Fail'">
                <i class="fas fa-times text-danger"></i> Failed
              </span>
              <span v-else-if="data.item.status === 'In Progress'">
                <i class="fas fa-spinner text-warning"></i> In Progress
              </span>
              <span v-else>
                {{ data.item.status }}
              </span>
            </div>
          </template>
        </b-table>
      </div>

      <div class="row">
        <div class="col-sm-12 col-md-6">
          <div id="tickets-table_length" class="dataTables_length">
            <label class="d-inline-flex align-items-center">
              {{ $t('show') }}
              <b-form-select
                class="form-select form-select-sm"
                v-model="perPage"
                size="sm"
                :options="pageOptions"
                @change="resetFilter"
              />
              {{ $t('entry') }}. {{ $t('show') }}&nbsp;<strong>{{
                filteredItems.length > 0 ? (currentPage - 1) * perPage + 1 : 0
              }}</strong>
              &nbsp;{{ $t('to') }}&nbsp;<strong>{{
                currentPage != totalPages
                  ? currentPage * perPage
                  : (currentPage - 1) * perPage + filteredItems.length
              }}</strong>
              &nbsp;{{ $t('from') }}&nbsp;<strong>{{ totalRows }}</strong
              >&nbsp;{{ $t('entry') }}.
            </label>
          </div>
        </div>
        <div class="col-sm-12 col-md-6">
          <div class="dataTables_paginate paging_simple_numbers float-end">
            <ul class="pagination pagination-rounded mb-0">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                @change="changePage"
              />
            </ul>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>
